import {
  metricDisplays,
  miniDisplays,
  traditionalCharts
} from './charts.js';

import modernLineChart from './line-modern-catalog.js';

import {
  funnelChart,
  heatmapChart,
  pyramidChart,
  scatterChart,
  treemapChart
} from './chart-composition-catalog.js';

import {
  gaugeQuickCharts
} from './gauge-catalog.js';

import {
  speedQuickCharts
} from './speed-catalog.js';

import {
  bulletQuickCharts
} from './bullet-catalog.js';

import {
  vuQuickCharts
} from './vu-catalog.js';

import {
  levelMeterQuickCharts,
  vuLedQuickCharts
} from './audio-meter-catalog.js';

/**
 * LAYOUT DA ABA GRÁFICOS
 *
 * Esta aba é catálogo visual, não composição de Dashboard. Displays e
 * FastCharts recebem respiro adicional para inspeção isolada. A aba Dashboard
 * demonstra a malha canônica em composição real. Medidores de áudio ficam em
 * seção própria porque o tutorial também demonstra fontes ao vivo.
 */

const line = modernLineChart;
const spline = traditionalCharts[1];
const area = traditionalCharts[2];
const sparea = traditionalCharts[3];
const barStacked = traditionalCharts[4];
const columnStacked = traditionalCharts[5];
const donut = traditionalCharts[6];
const bubble = traditionalCharts[7];
const radar = traditionalCharts[8];
const web = traditionalCharts[9];
const columnGrouped = traditionalCharts[10];
const pie = traditionalCharts[11];

const sourceRow = (label, controls) => ({
  element: 'div',
  style: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px'
  },
  content: [
    {
      element: 'span',
      text: label,
      style: {
        minWidth: '120px',
        fontSize: '12px',
        fontWeight: '600',
        opacity: '.82'
      }
    },
    ...controls
  ]
});

/**
 * Os meters têm proporções muito diferentes. Uma grade convencional cria linhas
 * com a altura do maior item e deixa buracos visuais enormes sob os menores.
 * Aqui usamos duas pilhas independentes, como um pequeno rack de instrumentos.
 */
const audioBoard = {
  element: 'div',
  style: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '10px',
    width: '100%'
  },
  content: [
    {
      element: 'div',
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px'
      },
      content: [
        vuQuickCharts[0],
        levelMeterQuickCharts[2]
      ]
    },
    {
      element: 'div',
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px'
      },
      content: [
        {
          element: 'div',
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '8px'
          },
          content: [
            vuLedQuickCharts[0],
            vuLedQuickCharts[1]
          ]
        },
        {
          element: 'div',
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '8px'
          },
          content: [
            levelMeterQuickCharts[0],
            levelMeterQuickCharts[1]
          ]
        }
      ]
    }
  ]
};

const chartsLayout = {
  element: 'group',
  type: 'fieldset',
  style: {
    width: '100%'
  },
  groups: [
    {
      legend: 'dic.tutorial.graphics.sections.summary',
      isGrid: false,
      content: [
        {
          element: 'div',
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '20px',
            width: '100%'
          },
          content: [
            ...miniDisplays,
            ...metricDisplays
          ]
        }
      ]
    },
    {
      legend: 'dic.tutorial.graphics.sections.quick',
      isGrid: false,
      content: [
        {
          element: 'div',
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '20px',
            width: '100%'
          },
          content: [
            ...gaugeQuickCharts,
            ...speedQuickCharts
          ]
        }
      ]
    },
    {
      legend: 'dic.tutorial.graphics.sections.bullets',
      content: bulletQuickCharts
    },
    {
      legend: 'dic.tutorial.graphics.sections.audio',
      content: [audioBoard]
    },
    {
      legend: 'dic.tutorial.graphics.sections.audioSources',
      content: [
        {
          element: 'div',
          id: 'tutorial-vu-live-controls',
          style: {
            display: 'grid',
            gap: '10px'
          },
          content: [
            sourceRow('dic.tutorial.graphics.audio.physicalSource', [
              {
                element: 'button',
                id: 'tutorial-vu-mic-start',
                type: 'button',
                mode: 'soft',
                text: 'dic.tutorial.graphics.audio.useInput'
              },
              {
                element: 'button',
                id: 'tutorial-vu-mic-stop',
                type: 'button',
                mode: 'soft',
                text: 'dic.tutorial.graphics.audio.stop',
                disabled: true
              },
              {
                element: 'span',
                id: 'tutorial-vu-mic-status',
                text: 'dic.tutorial.graphics.audio.stopped',
                style: {
                  fontSize: '12px',
                  opacity: '.72'
                }
              }
            ]),
            sourceRow('dic.tutorial.graphics.audio.tabSource', [
              {
                element: 'button',
                id: 'tutorial-vu-tab-start',
                type: 'button',
                mode: 'soft',
                text: 'dic.tutorial.graphics.audio.captureTab'
              },
              {
                element: 'button',
                id: 'tutorial-vu-tab-stop',
                type: 'button',
                mode: 'soft',
                text: 'dic.tutorial.graphics.audio.stop',
                disabled: true
              },
              {
                element: 'span',
                id: 'tutorial-vu-tab-status',
                text: 'dic.tutorial.graphics.audio.stopped',
                style: {
                  fontSize: '12px',
                  opacity: '.72'
                }
              }
            ])
          ]
        }
      ]
    },
    {
      legend: 'dic.tutorial.graphics.sections.trends',
      isGrid: true,
      cols: 2,
      content: [
        line,
        spline,
        area,
        sparea
      ]
    },
    {
      legend: 'dic.tutorial.graphics.sections.comparisons',
      isGrid: true,
      cols: 2,
      content: [
        columnGrouped,
        columnStacked,
        barStacked,
        bubble
      ]
    },
    {
      legend: 'dic.tutorial.graphics.sections.multidimensional',
      isGrid: true,
      cols: 2,
      content: [
        radar,
        web
      ]
    },
    {
      legend: 'dic.tutorial.graphics.sections.composition',
      isGrid: true,
      cols: 2,
      content: [
        donut,
        pie
      ]
    },
    {
      legend: 'dic.tutorial.graphics.sections.advanced',
      isGrid: true,
      cols: 2,
      content: [
        funnelChart,
        pyramidChart,
        scatterChart,
        heatmapChart,
        treemapChart
      ]
    }
  ]
};

export default chartsLayout;
